import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, X } from "lucide-react";
import api from "../lib/api";

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const knownNotificationIdsRef = useRef(null);

  const unlockAudio = useCallback(() => {
    const AudioContext =
      window.AudioContext || window.webkitAudioContext;

    if (!AudioContext) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      if (audioContextRef.current.state === "suspended") {
        audioContextRef.current.resume().catch(() => {});
      }

      audioUnlockedRef.current = true;
    } catch (e) {
      console.debug("Notification audio unavailable", e);
    }
  }, []);

  const playSelfOrderNotificationSound = useCallback(async () => {
    const AudioContext =
      window.AudioContext || window.webkitAudioContext;

    if (!AudioContext || !audioUnlockedRef.current) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      const ctx = audioContextRef.current;

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      if (ctx.state !== "running") return;

      const now = ctx.currentTime;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
      gain.connect(ctx.destination);

      const oscillator = ctx.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, now);
      oscillator.frequency.setValueAtTime(1174.66, now + 0.11);
      oscillator.connect(gain);

      oscillator.start(now);
      oscillator.stop(now + 0.34);
    } catch (e) {
      console.debug(
        "Failed to play Self Order notification sound",
        e
      );
    }
  }, []);

  const loadNotifications = useCallback(
    async ({ playSound = false } = {}) => {
      try {
        const res = await api.get("/notifications?limit=20&offset=0");
        const items = res?.data?.items || [];

        const currentIds = new Set(
          items.map((item) => item.id).filter(Boolean)
        );

        const previousIds = knownNotificationIdsRef.current;

        if (previousIds === null) {
          // Initial load establishes the baseline.
          // Existing notifications must NOT trigger sound.
          knownNotificationIdsRef.current = currentIds;
        } else {
          const newSelfOrderNotifications = items.filter(
            (item) =>
              item?.id &&
              !previousIds.has(item.id) &&
              item?.type === "SELF_ORDER_PAYMENT_SUBMITTED"
          );

          if (
            playSound &&
            newSelfOrderNotifications.length > 0
          ) {
            playSelfOrderNotificationSound();
          }

          knownNotificationIdsRef.current = currentIds;
        }

        setNotifications(items);
        setUnread(res?.data?.unread ?? 0);
      } catch (e) {
        console.error("Failed to load notifications", e);
      }
    },
    [playSelfOrderNotificationSound]
  );

  useEffect(() => {
    loadNotifications({ playSound: false });

    const timer = window.setInterval(() => {
      loadNotifications({ playSound: true });
    }, 30000);

    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  // Unlock browser audio after the cashier interacts with the app.
  useEffect(() => {
    const unlock = () => {
      unlockAudio();
    };

    document.addEventListener("pointerdown", unlock, {
      passive: true,
    });

    document.addEventListener("keydown", unlock);

    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [unlockAudio]);

  useEffect(() => {
    if (open) {
      // Opening the bell only refreshes the list.
      // It must never replay an already-known notification.
      loadNotifications({ playSound: false });
    }
  }, [open, loadNotifications]);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);

  const markRead = async (notification) => {
    try {
      if (!notification.is_read) {
        await api.post(`/notifications/${notification.id}/read`);

        setNotifications((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, is_read: true }
              : item
          )
        );

        setUnread((current) => Math.max(0, current - 1));
      }

      if (
        notification.related_type === "order" &&
        notification.related_id
      ) {
        setOpen(false);
        navigate(
          `/orders?order_id=${encodeURIComponent(notification.related_id)}`
        );
      }
    } catch (e) {
      console.error("Failed to process notification click", e);
    }
  };

  const dismiss = async (event, notification) => {
    event.stopPropagation();

    try {
      await api.post(`/notifications/${notification.id}/dismiss`);

      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id
            ? { ...item, dismissed_at: new Date().toISOString() }
            : item
        )
      );
    } catch (e) {
      console.error("Failed to dismiss notification", e);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="notification-bell"
        onClick={() => setOpen((current) => !current)}
        className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
        aria-label={unread > 0 ? `${t("Notifications")}, ${t("{{count}} unread", { count: unread })}` : t("Notifications")}
      >
        <Bell size={18} />

        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label={t("Close notifications")}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />

          <div
            data-testid="notification-dropdown"
            className="absolute right-0 mt-2 z-50 w-[min(380px,calc(100vw-2rem))] rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <p className="font-semibold text-sm">{t("Notifications")}</p>
                <p className="text-[11px] text-muted-foreground">
                  {unread > 0 ? t("{{count}} unread", { count: unread }) : t("All notifications are read")}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-secondary"
                aria-label={t("Close notifications")}
              >
                <X size={15} />
              </button>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {loading ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t("Loading notifications…")}
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t("No notifications")}
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`relative px-4 py-3 border-b border-border last:border-b-0 ${
                      notification.is_read
                        ? "bg-card"
                        : "bg-primary/5"
                    }`}
                    onClick={() => markRead(notification)}
                  >
                    <div className="pr-7">
                      <div className="flex items-start gap-2">
                        {!notification.is_read && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                        )}

                        <div className="min-w-0">
                          <p className="text-sm font-semibold">
                            {notification.title}
                          </p>

                          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                            {notification.message}
                          </p>

                          <p className="mt-2 text-[10px] text-muted-foreground">
                            {new Date(notification.created_at).toLocaleString(
                              "id-ID",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(event) => dismiss(event, notification)}
                      className="absolute top-3 right-3 w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary"
                      aria-label={t("Dismiss notification")}
                    >
                      <X size={13} />
                    </button>

                    {notification.is_read && (
                      <div className="absolute bottom-3 right-3 text-muted-foreground">
                        <Check size={12} />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
